import React, { useState, useMemo } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { IoSearchOutline, IoClose } from "react-icons/io5";
import Modal from "components/ui/Modal";

/**
 * One modal shell for every provider's cost drill-down.
 *
 * Rows expand in place rather than replacing the modal's contents, so you keep
 * sight of the parent while reading its children. Each level gets its own
 * column header strip when it opens, which keeps the numbers labelled however
 * deep you go.
 *
 * Shape it expects:
 *
 *   levels: [{ header, columns: [{ key, header, align, width }] }, ...]
 *   nodes:  [{ id, label, sublabel, muted, badge, values: {}, children: [] }]
 *
 * A node with `lazy: true` renders `renderLazy(node, depth)` instead of its
 * own children — that's how the AI providers fetch agents only for the
 * organization actually opened.
 */

const COL_W = "minmax(0,120px)";

const gridStyle = (columns) => ({
  gridTemplateColumns: `minmax(0,1fr) repeat(${columns.length}, ${COL_W})`,
});

const HeaderStrip = ({ level, depth }) => (
  <div
    className="grid gap-2 px-3 py-1.5 bg-lightergray border-y border-lightgray"
    style={{ ...gridStyle(level.columns), paddingLeft: `${12 + depth * 22}px` }}
  >
    <span className="text-xs font-semibold uppercase tracking-wide text-darkgray">
      {level.header}
    </span>
    {level.columns.map((c) => (
      <span
        key={c.key}
        className={`text-xs font-semibold uppercase tracking-wide text-darkgray ${
          c.align === "right" ? "text-right" : "text-left"
        }`}
      >
        {c.header}
      </span>
    ))}
  </div>
);

const TreeNode = ({ node, depth, levels, renderLazy, nodeActions }) => {
  const level = levels[depth];
  const childLevel = levels[depth + 1];

  const children = node.children ?? [];
  const expandable = node.lazy || children.length > 0;

  const [isOpen, setIsOpen] = useState(false);

  // When any column has a render function (interactive element like a select),
  // use a div for the row so nested interactive elements aren't inside a button.
  const hasRenderCol = level?.columns?.some((c) => c.render);
  const useDiv = (nodeActions && !expandable) || hasRenderCol;

  const toggle = expandable ? () => setIsOpen((v) => !v) : undefined;

  const rowContent = (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <span
        className={`flex items-center gap-2 min-w-0 ${useDiv && expandable ? "cursor-pointer" : ""}`}
        onClick={useDiv ? toggle : undefined}
      >
        <ChevronDown
          size={14}
          className={`shrink-0 transition-transform ${
            expandable ? "text-darkgray" : "text-transparent"
          } ${isOpen ? "" : "-rotate-90"}`}
        />
        <span className="min-w-0">
          <span
            className={`block truncate text-sm font-medium ${
              node.muted ? "text-darkgray italic" : "text-darkergray"
            }`}
          >
            {node.label}
          </span>
          {node.sublabel ? (
            <span className="block truncate text-xs text-darkgray">
              {node.sublabel}
            </span>
          ) : null}
        </span>
        {node.badge ? (
          <span className="shrink-0 text-xs uppercase tracking-wide bg-orange-100 text-orange-700 rounded px-1.5 py-0.5">
            {node.badge}
          </span>
        ) : null}
      </span>

      {level.columns.map((c) => {
        const className = `text-sm tabular-nums ${
          c.align === "right" ? "text-right" : "text-left"
        } ${c.strong ? "font-semibold text-darkergray" : "text-darkergray"}`;
        if (c.render) {
          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div key={c.key} className={className} onClick={(e) => e.stopPropagation()}>
              {c.render(node)}
            </div>
          );
        }
        return (
          <span key={c.key} className={className}>
            {node.values?.[c.key] ?? "—"}
          </span>
        );
      })}

      {nodeActions ? (
        <span className="flex items-center justify-end gap-1">
          {nodeActions(node)}
        </span>
      ) : null}
    </>
  );

  const rowStyle = {
    ...gridStyle(level.columns),
    ...(nodeActions ? { gridTemplateColumns: `${gridStyle(level.columns).gridTemplateColumns} auto` } : {}),
    paddingLeft: `${12 + depth * 22}px`,
  };

  return (
    <div>
      {useDiv ? (
        <div
          className={`w-full grid gap-2 items-center px-3 py-2.5 text-left border-b border-lightgray ${
            expandable ? "hover:bg-lightergray" : ""
          } cursor-default`}
          style={rowStyle}
        >
          {rowContent}
        </div>
      ) : (
        <button
          type="button"
          onClick={expandable ? () => setIsOpen((v) => !v) : undefined}
          disabled={!expandable}
          className={`w-full grid gap-2 items-center px-3 py-2.5 text-left border-b border-lightgray ${
            expandable ? "hover:bg-lightergray cursor-pointer" : "cursor-default"
          }`}
          style={rowStyle}
        >
          {rowContent}
        </button>
      )}

      {isOpen ? (
        node.lazy ? (
          renderLazy?.(node, depth + 1)
        ) : (
          <div>
            {childLevel ? <HeaderStrip level={childLevel} depth={depth + 1} /> : null}
            {children.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                levels={levels}
                renderLazy={renderLazy}
                nodeActions={nodeActions}
              />
            ))}
          </div>
        )
      ) : null}
    </div>
  );
};

export const CostBreakdownLoading = ({ text = "Loading…" }) => (
  <div className="flex items-center justify-center gap-2 py-8 text-sm text-darkgray">
    <Loader2 size={16} className="animate-spin" />
    {text}
  </div>
);

export const CostBreakdownEmpty = ({ text }) => (
  <div className="px-4 py-8 text-center text-sm text-darkgray">{text}</div>
);

/** Renders one already-loaded subtree — used by lazy loaders. */
export const CostBreakdownSubtree = ({ nodes, depth, levels, renderLazy }) => (
  <div>
    {levels[depth] ? <HeaderStrip level={levels[depth]} depth={depth} /> : null}
    {nodes.map((n) => (
      <TreeNode
        key={n.id}
        node={n}
        depth={depth}
        levels={levels}
        renderLazy={renderLazy}
      />
    ))}
  </div>
);

const CostBreakdownModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  summary = [],
  // Either pass a single tree (levels + nodes), or several via `views` — the
  // modal then shows a switch between them. Used where the same numbers can be
  // read two ways, e.g. AI spend by organization or by feature.
  views,
  levels = [],
  nodes = [],
  renderLazy,
  nodeActions,
  headerSlot,
  searchable = true,
  searchPlaceholder = "Search organizations…",
  emptyText = "Nothing to show for this period.",
  note,
}) => {
  const [query, setQuery] = useState("");
  const [viewKey, setViewKey] = useState(null);

  const viewList = views?.length
    ? views
    : [{ key: "default", label: "", levels, nodes, emptyText, searchPlaceholder }];
  const active =
    viewList.find((v) => v.key === viewKey) ?? viewList[0];

  const activeNodes = active.nodes ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activeNodes;
    return activeNodes.filter((n) =>
      String(n.label ?? "").toLowerCase().includes(q),
    );
  }, [activeNodes, query]);

  const close = () => {
    setQuery("");
    setViewKey(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={close} title={title} className="max-w-7xl">
      {subtitle ? (
        <p className="text-sm text-darkgray -mt-1 mb-4">{subtitle}</p>
      ) : null}

      {summary.length ? (
        <div className="flex flex-wrap gap-x-10 gap-y-3 px-4 py-3 mb-4 bg-lightergray border border-lightgray rounded-lg">
          {summary.map((s) => (
            <div key={s.label}>
              <div className="text-xs uppercase tracking-wide text-darkgray">
                {s.label}
              </div>
              <div
                className={`text-xl font-semibold tabular-nums ${
                  s.tone === "warn" ? "text-orange-600" : "text-darkergray"
                }`}
              >
                {s.value}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {headerSlot ? <div className="mb-3">{headerSlot}</div> : null}

      {active.note ?? note ? (
        <p className="text-xs text-darkgray italic mb-3">{active.note ?? note}</p>
      ) : null}

      {viewList.length > 1 ? (
        <div className="flex items-center gap-0.5 border border-mediumgray bg-white rounded p-0.5 mb-3 w-fit">
          {viewList.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => {
                setViewKey(v.key);
                setQuery("");
              }}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                active.key === v.key
                  ? "bg-darkergray text-white"
                  : "text-darkgray hover:bg-lightergray"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      ) : null}

      {searchable ? (
        <div className="relative mb-3">
          <IoSearchOutline
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-darkgray pointer-events-none"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={active.searchPlaceholder ?? searchPlaceholder}
            className="w-full pl-9 pr-9 py-2 text-sm border border-mediumgray rounded-lg text-darkergray placeholder:text-darkgray focus:outline-none focus:border-darkgray"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-darkgray hover:text-darkergray"
              aria-label="Clear search"
            >
              <IoClose size={16} />
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Modal already scrolls its body (max-h-[600px]); no nested scroller. */}
      <div className="border border-lightgray rounded-lg overflow-hidden">
        {active.levels?.[0] ? (
          <HeaderStrip level={active.levels[0]} depth={0} />
        ) : null}
        {filtered.length === 0 ? (
          <CostBreakdownEmpty
            text={query ? `No match for "${query}".` : active.emptyText ?? emptyText}
          />
        ) : (
          filtered.map((n) => (
            <TreeNode
              key={n.id}
              node={n}
              depth={0}
              levels={active.levels}
              renderLazy={renderLazy}
              nodeActions={nodeActions}
            />
          ))
        )}
      </div>

      {query && filtered.length > 0 ? (
        <p className="text-xs text-darkgray mt-2">
          {filtered.length} of {activeNodes.length} shown
        </p>
      ) : null}
    </Modal>
  );
};

export default CostBreakdownModal;
